// LogoCropperModal — react-easy-crop wrapper for cropping/repositioning uploaded logos.
// Returns a cropped Blob to the caller via onCropComplete.
import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

// Convert cropped area (pixels) into a Blob via a canvas.
const getCroppedBlob = (imageSrc, pixelCrop, mimeType = 'image/png') =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('Canvas is empty'));
        resolve(blob);
      }, mimeType, 0.95);
    };
    image.onerror = () => reject(new Error('Failed to load image for cropping'));
    image.src = imageSrc;
  });

const ASPECT_OPTIONS = [
  { label: 'Free (any ratio)', value: null },
  { label: 'Square 1:1', value: 1 },
  { label: 'Wide 3:1', value: 3 },
  { label: 'Banner 4:1', value: 4 },
  { label: 'Landscape 16:9', value: 16 / 9 },
];

const LogoCropperModal = ({ imageSrc, mimeType, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(3); // default 3:1 wide (good for horizontal logos)
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropCompleteCb = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedPixels, mimeType || 'image/png');
      onConfirm(blob);
    } catch (err) {
      console.error('Crop failed:', err);
      setProcessing(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      data-testid="logo-cropper-modal"
    >
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontWeight: 600, fontSize: 16, color: '#1F3864' }}>Crop &amp; Reposition Logo</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Drag to reposition · scroll or slider to zoom · pick an aspect ratio for uniform sizing</div>
        </div>

        {/* Crop canvas */}
        <div style={{ position: 'relative', width: '100%', height: 380, background: '#111', overflow: 'hidden' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect ?? undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropCompleteCb}
            restrictPosition={false}
            showGrid={true}
          />
        </div>

        {/* Controls */}
        <div style={{ padding: 20, borderTop: '1px solid #eee', display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>
                Zoom <span style={{ color: '#888' }}>({zoom.toFixed(2)}x)</span>
              </label>
              <input
                type="range" min={0.5} max={4} step={0.05} value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ width: '100%' }} data-testid="crop-zoom"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>
                Rotate <span style={{ color: '#888' }}>({rotation}°)</span>
              </label>
              <input
                type="range" min={-180} max={180} step={5} value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value, 10))}
                style={{ width: '100%' }} data-testid="crop-rotation"
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>
              Aspect Ratio
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ASPECT_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setAspect(opt.value)}
                  data-testid={`aspect-${opt.value ?? 'free'}`}
                  style={{
                    padding: '6px 12px',
                    fontSize: 12,
                    borderRadius: 20,
                    border: '1px solid #ddd',
                    background: aspect === opt.value ? '#1F3864' : '#fff',
                    color: aspect === opt.value ? '#fff' : '#333',
                    cursor: 'pointer',
                    fontWeight: aspect === opt.value ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
            <Button variant="outline" onClick={onCancel} disabled={processing} data-testid="crop-cancel">Cancel</Button>
            <Button onClick={handleConfirm} disabled={processing || !croppedPixels} data-testid="crop-confirm"
                    style={{ backgroundColor: '#1F3864', color: '#fff' }}>
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {processing ? 'Processing...' : 'Save Cropped Logo'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LogoCropperModal;
